/**
 * MenuRequestListContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import MenuRequestListPresenter from './MenuRequestListPresenter';
import { useGetMenus, useUpdateMenu } from 'hooks/MenuHooks';
import { useGetMenuCategories } from 'hooks/ProductCategoryHooks';
import { useGetStores } from 'hooks/ShopInformationHooks';
import { LoadingPage } from 'components';
import MessageAlert from 'utils';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

const MenuRequestListContainer = () => {
  /* ===== ROUTE ===== */
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const { updateMenu } = useUpdateMenu();

  /* ===== Data ===== */
  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({
    join_tables: '["menu_file"]',
  });
  // 메뉴 카테고리 전체 조회
  const { data: Categories } = useGetMenuCategories();
  // 매장 전체 조회
  const { data: Store } = useGetStores();

  /* ===== STATE ===== */
  const [data, setData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  // 모달 open 여부
  const [isOpened, setIsOpened] = useState(false);
  // 모달 타입
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = React.useState({
    menu: {},
    category: {},
  });
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 메뉴 파일
  const [files, setFiles] = useState([]);

  const [searchInputValue, setSearchInputValue] = React.useState({}); // 검색 입력값
  const [parameter, setParameter] = React.useState({}); // 검색 파라미터

  //메뉴 선택 데이터
  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */

  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, o) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  const handleSubmit = async (t, s = 'UPDATE', menu_id, application_status) => {
    const type = t.toLocaleUpperCase();
    const submit = s.toLocaleUpperCase();

    const submitType = {
      MENU: '메뉴',
      UPDATE: '수정',
    };

    const submitFunc = {
      MENU: {
        UPDATE: updateMenu,
      },
    };

    try {
      setIsLoading(true);

      let allSuccess = true;

      const { status } = await submitFunc[type][submit]({
        menu_id: menu_id,
        application_status: application_status,
      });

      if (status !== 200) {
        allSuccess = false;
      }

      if (allSuccess) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );
        setModalData((prev) => ({ ...prev, t: {} }));
        setIsOpened(false);
        window.location.reload();
      } else {
        MessageAlert.warning(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
        );
      }
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 검색 필터
   * --
   */
  const handleSearch = () => {
    const mergedParams = Object.entries({
      ...Object.fromEntries(searchParams.entries()), // 기존 searchParams 객체화
      ...searchInputValue, // 새로운 입력값 병합
      // page: 1, // 검색 시 페이지 값 초기화
    }).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value; // null이나 빈 문자열이 아닌 값만 추가
      }
      return acc;
    }, {});

    // 병합된 객체를 쿼리 스트링으로 변환
    const newSearchParams = new URLSearchParams(mergedParams).toString();

    // URL 업데이트
    navigate(`${location.pathname}?${newSearchParams}`, {
      replace: true,
    });
  };

  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string | number | boolean} v
   * @param {func} callback
   */
  const handleSearchChange = (t, v, callback = null) => {
    setSearchInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    const NewCategories = Categories?.data?.filter(
      (item) => item.application_status === 'APPROVE'
    );

    const newData = Menus?.data
      ?.filter((i) => i.shop_information_id != null)
      ?.map((menu) => {
        const category = NewCategories?.find(
          (category) =>
            category.product_category_id === menu.product_category_id
        );

        const store_data = Store?.data.find(
          (store) => store.shop_information_id === menu.shop_information_id
        );
        return {
          ...menu,
          category_name: category?.product_category_name || '',
          shop_name: store_data?.shop_information_name || '',
        };
      });

    // const menuData = NewCategories?.map((category) => ({
    //   menus: Menus?.data
    //     ?.filter(
    //       (menu) =>
    //         menu?.product_category_id === category?.product_category_id &&
    //         menu?.shop_information_id === null &&
    //         menu?.application_status === 'APPLY'
    //     )
    //     .map((menu) => ({
    //       ...menu,
    //       category_name: category.product_category_name, // category_name을 menu 데이터에 추가
    //     })),
    // }));

    // setMenuData(menuData);
    setCategoryData(NewCategories);
    setData(newData);
  }, [Menus, Categories, Store]);

  /**
   * params 값 반영
   * --
   */
  React.useEffect(() => {
    const allParams = Object.fromEntries(searchParams.entries());
    setSearchInputValue(allParams);
    setParameter(allParams);
  }, [searchParams]);

  console.log(data);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <MenuRequestListPresenter
        data={data}
        categoryData={categoryData}
        isOpened={isOpened}
        modalData={modalData}
        modalType={modalType}
        files={files}
        onOpen={handleModal}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onFileChange={setFiles}
        onSearch={handleSearch}
        searchInputValue={searchInputValue}
        parameter={parameter}
        navigate={navigate}
        onSearchChange={handleSearchChange}
      />
    </>
  );
};

export default MenuRequestListContainer;
